import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppOutbox, AppSendRequest } from "./AppOutbox";
import { IComposerSent } from "./AppComposer";
import { AppTrChat, AppTranslateRules } from "./AppTranslateRules";
import { AppTranslator, IAppTrHeard } from "./AppTranslator";

// AppTranslateSend - THE ONE PLACE A SEND LEARNS WHETHER IT CARRIES `translate: true` (translation, owner 2026-09-27).
// `AppMain.QueueTo` - the page half of EVERY send (the box, an AI draft's Send, a task-close card's Send, a failed
// bubble's Retry / Send in English, all through `AppComposer.SendText`) - hands the send here; this decides, builds the
// request with the flag stamped on it, and hands it back to the page to write (`IAppTrSendPort.Write`).
//
// ⭐ WHY A CLASS OF ITS OWN: the decision is the whole of "the owner's words never go out untranslated by surprise",
// and `AppMain` is DOM wiring no suite can load. Here the REAL decision and the REAL request are driven by the suite
// (`AppTranslate.test.ts`), so dropping the flag, guessing "no" for a chat not known, or writing after a lost answer
// for a chat last seen ON each fail a test.
//
// ⛔ DECISION 9: this writes exactly the ONE request the owner already pressed Send for - once, or not at all. It is
//    reached only from `AppComposer.SendText` (the ONE sender, ONE latch); nothing here is started by an answer.
// ⛔ "ask" (the visit does not know the chat yet): the ticket is HELD, `/translate get` is asked, and only its answer
//    writes the request (`Known`). Still "ask" after the answer = a LOST answer for a chat last seen ON: refused with
//    `AppTranslateRules.LostWhy` - never written untranslated.
// ⚠ SHAPED BY AN INTERFACE (`IAppTrSendPort`) so the suite plays the page.

/** One send's facts - who it is for and what the owner typed. */
export class AppTrSendJob {
    /** The chat's page href (the key `AppTrBook` knows it by). */
    public Href: string = "";
    public ChatId: string = "";
    public Title: string = "";
    /** EXACTLY what the owner typed (the service decides the outgoing words). */
    public Text: string = "";
    /** Who approved it (`AppSource.Who`) - never defaulted here. */
    public Approver: string = "";
}

/** The page's side of a send. */
export interface IAppTrSendPort {
    /** Write this request (already stamped) and answer `Then` once, later. "" = on its way, else the sentence. */
    Write(Req: AppSendRequest, Then: IComposerSent): string;
    /** The send waits for the chat's state: hold `Then` as the page's one ticket, the button busy, one line. */
    Hold(Then: IComposerSent): void;
    /** The wait ended: the held ticket (null when something already took it), the button free again. */
    Release(): IComposerSent | null;
    Say(Text: string, Bad: boolean): void;
}

export class AppTrSendGate {
    public static readonly CheckingWords: string = "Checking this chat's translation…";
    private Translator: AppTranslator;

    public constructor(Translator: AppTranslator) {
        this.Translator = Translator;
    }

    /**
     * ⭐ A SEND BEGINS. `Asked` is the owner's own choice on a failed bubble ("yes" Retry / "no" Send in English), ""
     * otherwise (as the chat's chip says). "" when the request is on its way (or waits for the chat's state), else the
     * sentence to show - and then `Then` is never answered.
     */
    public Begin = (Job: AppTrSendJob, Asked: string, Then: IComposerSent, Port: IAppTrSendPort): string => {
        if (getCoreUtils().KErrorMode === true) { return AppTranslateRules.LostWhy; }
        try {
            const Mode: string = this.Translator.ModeFor(Job.Href, nz(Asked));
            if (Mode !== "ask") { return this.Write(Job, Then, Port, Mode === "yes"); }
            Port.Hold(Then);
            Port.Say(AppTrSendGate.CheckingWords, false);
            this.Translator.Ask(Job.Href, new AppTrSendWait(this, Job, Port));
            return "";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTrSendGate.Begin");
            return AppTranslateRules.LostWhy;
        }
    };

    /** The chat's state arrived for a send that waited on it: write it now, or say why not. */
    public Known = (Job: AppTrSendJob, Port: IAppTrSendPort): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Then: IComposerSent | null = Port.Release();
            if (Then === null) { return; }
            // ⛔ Still "ask" after the answer = lost for a chat last seen ON: refused. Lost for any other chat = "no".
            const Mode: string = this.Translator.ModeFor(Job.Href, "");
            let Why: string = AppTranslateRules.LostWhy;
            if (Mode !== "ask") { Why = this.Write(Job, Then as IComposerSent, Port, Mode === "yes"); }
            if (Why.length === 0) { return; }
            Port.Say(Why, true);
            (Then as IComposerSent).Failed(Why);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTrSendGate.Known");
        }
    };

    /** Build the request, stamp the flag, hand it to the page. */
    private Write = (Job: AppTrSendJob, Then: IComposerSent, Port: IAppTrSendPort, Translate: boolean): string => {
        if (getCoreUtils().KErrorMode === true) { return AppTranslateRules.LostWhy; }
        try {
            const Why: string[] = [];
            const Req: AppSendRequest | null = AppOutbox.Build(Job.ChatId, Job.Title, Job.Text, Job.Approver, Why);
            if (Req === null) { return Why.join(" "); }
            (Req as AppSendRequest).Translate = Translate;
            return Port.Write(Req as AppSendRequest, Then);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTrSendGate.Write");
            return AppTranslateRules.LostWhy;
        }
    };
}

/**
 * A send that waited for its chat's translation state - carried to `AppTrSendGate.Known`. A class, not a closure.
 * ⛔ ANSWERED ONCE: it writes only the ONE request the owner already pressed Send for.
 */
export class AppTrSendWait implements IAppTrHeard {
    private Gate: AppTrSendGate;
    private Job: AppTrSendJob;
    private Port: IAppTrSendPort;
    private Done: boolean = false;

    public constructor(Gate: AppTrSendGate, Job: AppTrSendJob, Port: IAppTrSendPort) {
        this.Gate = Gate;
        this.Job = Job;
        this.Port = Port;
    }

    public Heard = (Href: string, Chat: AppTrChat): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Done === true) { return; }
            this.Done = true;
            this.Gate.Known(this.Job, this.Port);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTrSendWait.Heard");
        }
    };
}
